import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LinkButton from './LinkButton';
import { makeMatcher } from '../searchMatch';
import './PlatformListings.css';

// Platform Listings: one row per PROD quest, one column per sales channel, so
// the whole mapping is readable at a glance. The auto-answer bot reads these
// rows to tell which quest an incoming order belongs to.
//
// Every channel has a stable numeric id for the listing and that id is the only
// thing matched on — Sputnik's activity.id and Tripster's experience.id both
// arrive inside the order payload, a site's is the WooCommerce product id.
// Nothing is typed by hand: paste the listing link and the server reads the id
// and the title off the platform itself. Titles are shown for humans only.
//
// Test quests can be listed too — a quest goes to moderation, sometimes even on
// sale, while it still lives only in quests_test. Such a quest gets a row as
// soon as it holds a listing, and every test quest is offered when mapping.
//
// On every open the partner accounts are pulled (through the auto-answer bot's
// sessions on the same host) and the statuses and titles in the table are
// brought in step with them — the platform is the truth, not a badge clicked
// here.
//
// With dozens of quests the table needs a filter and a way to drop the rows
// that are done: the bar on top holds both, and it stays put while the page
// scrolls so Close and Refresh are always within reach.

// A site sells the quests of its own country only, so for a quest from
// elsewhere that column is not expected to be filled — `country` says which.
const PLATFORMS = [
  {
    key: 'sputnik',
    label: 'Sputnik8',
    idLabel: 'activity id',
    hint: 'Paste a link like https://www.sputnik8.com/ru/activities/96984',
    account: true,
  },
  {
    key: 'tripster',
    label: 'Tripster',
    idLabel: 'experience id',
    hint: 'Paste a link like https://experience.tripster.ru/experience/124254/',
    account: true,
  },
  {
    key: 'site',
    label: 'istanbul.quest',
    idLabel: 'product id',
    hint: 'Paste the product page or an add-to-cart link on istanbul.quest',
    account: false,
    country: 'Turkey',
  },
  {
    key: 'moroccan',
    label: 'moroccan.quest',
    idLabel: 'product id',
    hint: 'Paste the product page or an add-to-cart link on moroccan.quest',
    account: false,
    country: 'Morocco',
  },
];

// Is this column expected to hold a listing for this quest? Partners sell
// everything; a site only its own country.
const expectedOn = (p, quest) => p.account || (!!p.country && quest.country === p.country);

// Status must be readable without relying on hue: each one gets its own glyph
// and its own fill style (solid / outlined / muted), and the two colours are
// blue vs orange — the pair that survives red-green colour blindness, unlike the
// green/amber it replaced.
const STATUS_LABEL = {
  active: 'published', moderation: 'in moderation', archived: 'delisted', draft: 'draft',
};
const STATUS_GLYPH = { active: '●', moderation: '◍', archived: '○', draft: '◌' };
const STATUS_ORDER = ['active', 'moderation', 'archived', 'draft'];

// Never leave a date to the host's default locale — that is what turned a
// delivery on the 25th of August into "8/25/2026". Day first, month by name, so
// there is nothing to misread.
const DATE_FMT = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric',
});
const fmtDate = (value) => (value ? DATE_FMT.format(new Date(value)) : '');

// The id a test quest gets in prod — the same rule the push-to-prod button
// uses (QuestDiffViewer): drop the _test suffix, or keep the id as it is.
const prodIdOf = (id) => (id.endsWith('_test') ? id.slice(0, -5) : id);

// The filter and the "hide fully mapped" switch survive a reload; a private
// window simply keeps them for the page.
const FILTER_KEY = 'platformListings.filter';
const HIDE_FULL_KEY = 'platformListings.hideFull';
const readStored = (key, fallback) => {
  try { const v = localStorage.getItem(key); return v === null ? fallback : v; } catch (e) { return fallback; }
};
const store = (key, value) => {
  try { localStorage.setItem(key, value); } catch (e) { /* private window */ }
};

// Searchable stand-in for the "map to quest" <select>: with dozens of quests
// a plain dropdown is unreadable. Type part of a name, id or city — the wrong
// keyboard layout counts too (searchMatch) — then Enter or a click maps it.
const QuestPicker = ({ options, disabled, onPick }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const boxRef = useRef(null);
  const listRef = useRef(null);

  const rows = useMemo(() => {
    const all = [
      ...options.prod.map((q) => ({ ...q, is_test: false })),
      ...options.test.map((q) => ({ ...q, is_test: true })),
    ];
    if (!query.trim()) return all;
    const match = makeMatcher(query);
    return all
      .map((q) => ({ q, score: match([q.quest_name || '', q.quest_id, q.city || '', q.country || '']) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((r) => r.q);
  }, [options, query]);

  useEffect(() => { setCursor(0); }, [query]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[cursor];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [open, cursor]);

  // A click anywhere else folds the list.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const pick = (q) => {
    setOpen(false);
    setQuery('');
    onPick(q.quest_id);
  };

  return (
    <div className="plat-picker" ref={boxRef}>
      <input
        type="text"
        value={query}
        disabled={disabled}
        placeholder="map to quest: name, id or city…"
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setCursor((c) => Math.min(c + 1, Math.max(rows.length - 1, 0)));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setCursor((c) => Math.max(c - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (open && rows[cursor]) pick(rows[cursor]);
          } else if (e.key === 'Escape') {
            // First Esc folds the list, the next one leaves the field — and
            // only then does Esc reach the page and close it.
            if (open) setOpen(false); else e.target.blur();
          }
        }}
      />
      {open && (
        <ul className="plat-picker-list" role="listbox" ref={listRef}>
          {rows.length === 0 && <li className="plat-picker-empty">no quest matches</li>}
          {rows.map((q, i) => (
            <li
              key={q.quest_id}
              role="option"
              aria-selected={i === cursor}
              className={`plat-picker-item${i === cursor ? ' is-active' : ''}`}
              onMouseEnter={() => setCursor(i)}
              onMouseDown={(e) => { e.preventDefault(); pick(q); }}
            >
              <span className="plat-picker-name">
                {q.quest_name || q.quest_id}
                {q.is_test && <span className="plat-test">test</span>}
              </span>
              <span className="plat-picker-meta">
                {q.city ? `${q.city} · ` : ''}<code>{q.quest_id}</code>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const KIND_LABEL = {
  unlock: 'unlock link',
  app_promo: 'promo code for the app',
  site_promo: 'promo code for the site (remainder paid on the site)',
  hold_notice: 'hold notice — quest not open yet',
  escalated: 'escalated to Telegram',
};
const fmtDateTime = (value) => (value ? new Intl.DateTimeFormat('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}).format(new Date(value)) : '');
const userLabel = (u) => {
  if (!u) return '';
  const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return name || (u.username ? `@${u.username}` : `id ${u.user_id}`);
};

// The orders behind a cell's counter, in a modal like the console's user card:
// one row per order with a link into the partner cabinet (names and tour dates
// live there, not in our tables), what the bot handed out, and who redeemed it.
// 'YYYY-MM-DD' → '12 Nov 2026', built from the parts so no time zone can
// shift the day.
const fmtTourDate = (ymd) => {
  const m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return ymd || '';
  return DATE_FMT.format(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
};

const OrdersModal = ({ quest, platform, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Per-order failures of the live pull and how many were left for next time.
  const [pullErrors, setPullErrors] = useState([]);
  const [notPulled, setNotPulled] = useState(0);

  useEffect(() => {
    let alive = true;
    const q = new URLSearchParams({ quest_id: quest.quest_id, platform: platform.key });
    fetch(`/api/platform-listings/orders?${q}`, { credentials: 'include' })
      .then(async (r) => {
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || `Failed to load orders (${r.status})`);
        if (!alive) return;
        setData(j.orders || []);
        setPullErrors(j.errors || []);
        setNotPulled(j.not_pulled || 0);
      })
      .catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [quest.quest_id, platform.key]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <div className="plat-modal-overlay" onClick={onClose}>
      <div className="plat-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button className="plat-modal-close" onClick={onClose} title="Close (Esc)">×</button>
        <h2>
          <span className={`plat-platform plat-platform-${platform.key}`}>{platform.label}</span>
          {' '}{quest.quest_name || quest.quest_id}
        </h2>
        <div className="plat-sub">
          {platform.account
            ? <>Orders the bot handled for this quest on {platform.label}: who booked, for which
              day, what was paid, what the bot handed out and who redeemed it. Details the bot
              did not store yet are pulled from {platform.label} on open and kept.</>
            : <>Orders for this quest on {platform.label}. Only the unlock token is held here —
              the customer and the amounts are in the WooCommerce admin, each order links there.</>}
        </div>
        {error && <div className="plat-error">{error}</div>}
        {(pullErrors || []).map((msg) => <div className="plat-error" key={msg}>{msg}</div>)}
        {notPulled > 0 && (
          <div className="plat-sub">
            {notPulled} older order{notPulled === 1 ? '' : 's'} not pulled from {platform.label} this
            time (a bounded batch per open) — open again to continue.
          </div>
        )}
        {!data && !error && <div className="plat-modal-loading">Loading…</div>}
        {data && data.length === 0 && <div className="plat-none">No orders recorded.</div>}
        {data && data.length > 0 && (
          <table className="plat-orders-table">
            <thead>
              <tr>
                <th>Dates</th><th>Order</th>
                {platform.account && <><th>Client</th><th>Paid</th></>}
                <th>Issued</th><th>Redeemed in the app</th>
              </tr>
            </thead>
            <tbody>
              {data.map((o) => {
                const d = o.details;
                const rem = o.remainder;
                return (
                  <tr
                    key={`${o.order_id}|${o.code}`}
                    className={[
                      d && d.status === 'canceled' ? 'plat-order-canceled' : '',
                      o.test ? 'plat-order-test' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    {/* Every date of the order, one under another: when it was
                        bought, when the tour is, when the remainder reached us. */}
                    <td className="plat-nowrap plat-dates">
                      <span className="plat-date-row">
                        <span className="plat-date-key">bought</span>
                        {fmtDateTime(o.created_at)}
                      </span>
                      {platform.account && (
                        <span className="plat-date-row">
                          <span className="plat-date-key">tour</span>
                          {d && d.tour_date
                            ? <>{fmtTourDate(d.tour_date)}{d.tour_time ? ` ${String(d.tour_time).slice(0, 5)}` : ''}
                              {d.persons ? <span className="plat-id"> · {d.persons} pax</span> : null}</>
                            : <span className="plat-none" title={o.fetch_error || 'no details yet'}>—</span>}
                        </span>
                      )}
                      {rem && rem.where === 'partner_remainder' && (
                        <span className="plat-date-row">
                          <span className="plat-date-key">paid</span>
                          <span className="plat-id">remainder of a partner order #{rem.partner_order_id}</span>
                        </span>
                      )}
                      {rem && rem.where === 'app' && (
                        <span className="plat-date-row">
                          <span className="plat-date-key">paid</span>{fmtDateTime(rem.at)} <span className="plat-id">in the app</span>
                        </span>
                      )}
                      {rem && rem.where === 'site' && (
                        <span className="plat-date-row">
                          <span className="plat-date-key">paid</span>
                          <a href={rem.site_order_url} target="_blank" rel="noreferrer">on the site, order #{rem.site_order_id} ↗</a>
                        </span>
                      )}
                      {rem && rem.where === 'none' && (
                        <span className="plat-date-row">
                          <span className="plat-date-key">paid</span>
                          <span className="plat-id">
                            {o.test
                              ? `${o.amount} ${o.currency || 'RUB'} — a checkout test, not a sale`
                              : o.amount !== null && o.amount !== undefined
                                ? `${o.amount} ${o.currency || 'RUB'} in full on ${platform.label}`
                                : `in full on ${platform.label}`}
                          </span>
                        </span>
                      )}
                      {!rem && !(d && d.status === 'canceled') && (
                        <span className="plat-date-row"><span className="plat-date-key">paid</span><span className="plat-sold-pending">remainder pending</span></span>
                      )}
                    </td>
                    <td className="plat-nowrap">
                      {o.order_url
                        ? <a href={o.order_url} target="_blank" rel="noreferrer">#{o.order_id} ↗</a>
                        : `#${o.order_id || '—'}`}
                      {d && d.status && <span className={`plat-id plat-order-status-${d.status}`}> {d.status}</span>}
                    </td>
                    {platform.account && (
                      <>
                        <td>
                          {d && d.client_name ? d.client_name : <span className="plat-none">—</span>}
                          {d && (d.client_email || d.client_phone) && (
                            <span className="plat-id plat-contacts">
                              {[d.client_email, d.client_phone].filter(Boolean).join(' · ')}
                            </span>
                          )}
                        </td>
                        <td className="plat-nowrap">
                          {d && d.total_price !== null && d.total_price !== undefined
                            ? <>{d.total_price} {d.currency || ''}
                              {d.prepay ? <span className="plat-id"> prepaid {d.prepay}</span> : null}</>
                            : <span className="plat-none">—</span>}
                        </td>
                      </>
                    )}
                    <td>
                      {KIND_LABEL[o.kind] || o.kind}
                      {o.kind === 'escalated' && o.code && <span className="plat-id"> — {o.code}</span>}
                      {o.kind !== 'escalated' && o.code && (
                        <span className="plat-id" title={o.url || undefined}> · <code>{o.code}</code></span>
                      )}
                    </td>
                    <td>
                      {o.used_by
                        ? <>{userLabel(o.used_by)} <span className="plat-id">{fmtDateTime(o.used_at)}</span></>
                        : <span className="plat-none">not yet</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const PlatformListings = ({ onClose, closeHref }) => {
  const [quests, setQuests] = useState([]);
  const [testQuests, setTestQuests] = useState([]);
  const [listings, setListings] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  // Sales per quest by the agreed definition (full price paid), see the
  // server's salesSummary: { total, by: {sputnik, tripster, site, app},
  // pending, unknown, cancelled, last_sale_at }.
  const [sales, setSales] = useState({});
  const [salesWarning, setSalesWarning] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Which cell has its link box open: `${questId}|${platform}`.
  const [openCell, setOpenCell] = useState(null);
  const [linkDraft, setLinkDraft] = useState('');
  const [busy, setBusy] = useState(null);
  // What the partner accounts hold, per platform:
  // { sputnik: [{external_id, title, url, status, platform_status, city, quest_id}], tripster: [...] }
  const [available, setAvailable] = useState({});
  // Per-platform failure of the last pull, kept whole — an expired session and
  // a changed JSON shape need different fixes.
  const [syncErrors, setSyncErrors] = useState({});
  // Ids of rows whose listing the account no longer returns.
  const [missing, setMissing] = useState(new Set());
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState(() => readStored(FILTER_KEY, ''));
  const [hideFull, setHideFull] = useState(() => readStored(HIDE_FULL_KEY, '0') === '1');
  // The cell whose orders are open in the modal: { quest, platform } or null.
  const [ordersView, setOrdersView] = useState(null);
  const closeOrders = useCallback(() => setOrdersView(null), []);

  // Pull both accounts; rows the server updated replace ours, so the table
  // shows the platform's status without a reload.
  const sync = useCallback(async () => {
    setSyncing(true);
    try {
      const r = await fetch('/api/platform-listings/sync', {
        method: 'POST', credentials: 'include',
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `Could not pull the platforms (${r.status})`);
      setAvailable(j.available || {});
      setSyncErrors(j.errors || {});
      setMissing(new Set(j.missing || []));
      if (j.updated && j.updated.length) {
        const byId = new Map(j.updated.map((u) => [u.id, u]));
        setListings((prev) => prev.map((l) => byId.get(l.id) || l));
      }
    } catch (e) {
      setSyncErrors({ sync: e.message });
    } finally {
      setSyncing(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch('/api/platform-listings', { credentials: 'include' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `Failed to load (${r.status})`);
      setQuests(j.quests || []);
      setTestQuests(j.testQuests || []);
      setListings(j.listings || []);
      setDeliveries(j.deliveries || []);
      setSales(j.sales || {});
      setSalesWarning(j.salesWarning || null);
      setCanEdit(!!j.canEdit);
      setLoading(false);
      // Writing the platform's statuses needs the edit right; read-only users
      // see the table as it was last synced by someone who has it.
      if (j.canEdit) await sync();
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  }, [sync]);

  useEffect(() => { load(); }, [load]);

  // Esc closes the page — unless a field has the focus or a link box is open,
  // those take their own Esc first.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented || openCell || ordersView) return;
      const tag = e.target && e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (onClose) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openCell, ordersView, onClose]);

  const cellListings = useCallback((questId, platform) => listings.filter(
    (l) => l.quest_id === questId && l.platform === platform
  ), [listings]);

  const ordersFor = useCallback((questId, platform) => (
    deliveries.find((d) => d.quest_id === questId && d.platform === platform) || null
  ), [deliveries]);

  const orphans = useMemo(() => {
    const known = new Set(quests.map((q) => q.quest_id));
    return listings.filter((l) => !known.has(l.quest_id));
  }, [listings, quests]);

  // A row is fully mapped when every column expected for it holds a listing:
  // both partners, plus the site of the quest's own country. Any row counts —
  // a Sputnik listing may carry a title and no id.
  const fullyMapped = useCallback((q) => PLATFORMS.every(
    (p) => !expectedOn(p, q)
      || listings.some((l) => l.quest_id === q.quest_id && l.platform === p.key)
  ), [listings]);

  // Rows that pass the filter and the "hide fully mapped" switch. The filter
  // also looks at the listings' titles and ids, so a row is found by what the
  // platform calls it.
  const visible = useMemo(() => {
    const typed = filter.trim().length > 0;
    const match = makeMatcher(filter);
    return quests.filter((q) => {
      if (hideFull && fullyMapped(q)) return false;
      if (!typed) return true;
      const own = listings.filter((l) => l.quest_id === q.quest_id);
      return match([
        q.quest_name || '', q.quest_id, q.city || '', q.country || '',
        ...own.map((l) => l.title || ''),
        ...own.map((l) => l.external_id || ''),
      ]) > 0;
    });
  }, [quests, listings, filter, hideFull, fullyMapped]);

  const fullCount = useMemo(() => quests.filter(fullyMapped).length, [quests, fullyMapped]);

  // Row spans for the city column: the first quest of a city carries the label
  // for the whole group, the rest carry nothing. Prod rows come first, test
  // rows after them, so a test quest never merges into a prod city group.
  // Computed over the visible rows, so hiding a row never breaks a span.
  const cityGroups = useMemo(() => {
    const spans = new Map();
    let i = 0;
    while (i < visible.length) {
      const city = visible[i].city || '';
      const isTest = !!visible[i].is_test;
      let n = 1;
      while (
        i + n < visible.length
        && (visible[i + n].city || '') === city
        && !!visible[i + n].is_test === isTest
      ) n += 1;
      spans.set(visible[i].quest_id, { city, span: n, country: visible[i].country });
      i += n;
    }
    return spans;
  }, [visible]);

  const firstTestRow = useMemo(() => (visible.find((q) => q.is_test) || {}).quest_id, [visible]);

  // Column counters: quests listed / quests where a listing is expected.
  const totals = useMemo(() => {
    const t = {};
    PLATFORMS.forEach((p) => {
      t[p.key] = {
        have: quests.filter((q) => listings.some(
          (l) => l.quest_id === q.quest_id && l.platform === p.key
        )).length,
        expected: quests.filter((q) => expectedOn(p, q)).length,
      };
    });
    return t;
  }, [listings, quests]);

  // Choices for "map to quest": every prod quest, then every test quest.
  const questOptions = useMemo(() => {
    const prod = quests.filter((q) => !q.is_test);
    const seen = new Set(prod.map((q) => q.quest_id));
    const test = [
      ...quests.filter((q) => q.is_test),
      ...testQuests.filter((q) => !quests.some((r) => r.quest_id === q.quest_id)),
    ].filter((q) => !seen.has(q.quest_id));
    return { prod, test };
  }, [quests, testQuests]);

  const saveRow = async (row) => {
    const r = await fetch(
      row.id ? `/api/platform-listings/${row.id}` : '/api/platform-listings',
      {
        method: row.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(row),
      }
    );
    const j = await r.json();
    if (!r.ok) throw new Error(j.error || `Failed to save (${r.status})`);
    setListings((prev) => (row.id ? prev.map((l) => (l.id === j.id ? j : l)) : [...prev, j]));
    return j;
  };

  // A test quest that just received its first listing needs a row of its own;
  // the server decides which test quests get one, so ask it again.
  const ensureRow = async (questId) => {
    if (quests.some((q) => q.quest_id === questId)) return;
    const r = await fetch('/api/platform-listings', { credentials: 'include' });
    const j = await r.json();
    if (r.ok) setQuests(j.quests || []);
  };

  const markMapped = (platform, externalId, questId) => {
    setAvailable((prev) => ({
      ...prev,
      [platform]: (prev[platform] || []).map(
        (e) => (e.external_id === externalId ? { ...e, quest_id: questId } : e)
      ),
    }));
  };

  // Paste a link → the server reads the id and the title from the platform, and
  // the row is stored right away. One step, nothing to retype.
  const attachByLink = async (questId, platform, url) => {
    const key = `${questId}|${platform}`;
    setBusy(key);
    setError(null);
    try {
      const r = await fetch('/api/platform-listings/resolve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ platform, url }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `Could not read that link (${r.status})`);
      const existing = cellListings(questId, platform)[0];
      // The account pull knows the listing's real status; a pasted link does not.
      const known = (available[platform] || []).find((e) => e.external_id === j.external_id);
      await saveRow({
        id: existing ? existing.id : null,
        platform,
        quest_id: questId,
        external_id: j.external_id,
        title: j.title,
        url: j.url || url,
        status: known ? known.status : (existing ? existing.status : 'active'),
        notes: existing ? existing.notes : null,
      });
      if (known) markMapped(platform, j.external_id, questId);
      setOpenCell(null);
      setLinkDraft('');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const attachById = async (questId, platform, entry) => {
    const key = `${questId}|${platform}`;
    setBusy(key);
    setError(null);
    try {
      const existing = cellListings(questId, platform)[0];
      await saveRow({
        id: existing ? existing.id : null,
        platform,
        quest_id: questId,
        external_id: entry.external_id,
        title: entry.title,
        url: entry.url,
        status: entry.status || (existing ? existing.status : 'active'),
        notes: existing ? existing.notes : null,
      });
      markMapped(platform, entry.external_id, questId);
      await ensureRow(questId);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const cycleStatus = async (listing) => {
    const next = STATUS_ORDER[(STATUS_ORDER.indexOf(listing.status) + 1) % STATUS_ORDER.length];
    setBusy(`status:${listing.id}`);
    setError(null);
    try {
      await saveRow({ ...listing, status: next });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (listing) => {
    const label = listing.title || listing.external_id;
    if (!window.confirm(`Unlink “${label}”?\nThe bot will stop recognising orders for this listing.`)) return;
    setBusy(`del:${listing.id}`);
    setError(null);
    try {
      const r = await fetch(`/api/platform-listings/${listing.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `Failed to delete (${r.status})`);
      setListings((prev) => prev.filter((l) => l.id !== listing.id));
      markMapped(listing.platform, listing.external_id, null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const platformOf = (key) => PLATFORMS.find((p) => p.key === key) || { label: key };

  // Partner statuses are synced from the account; the sites have no such feed,
  // so their badge stays a hand switch.
  const statusTitle = (platform, status) => {
    const p = platformOf(platform);
    if (!canEdit) return STATUS_LABEL[status];
    return p.account
      ? `${STATUS_LABEL[status]} — as on ${p.label}; click to override until the next pull`
      : 'Click to change status';
  };

  // "612 sold" under the quest name: full-price sales across every channel,
  // the breakdown and the deposits still awaiting their remainder in the tooltip.
  const renderSold = (q) => {
    const s = sales[q.quest_id];
    if (!s) return null;
    const by = s.by || {};
    const lines = [
      `Sputnik ${by.sputnik || 0} · Tripster ${by.tripster || 0} · istanbul.quest ${by.site || 0} · app ${by.app || 0}`,
      'A sale = the full price paid: on the platform, on the site / in the app, or a platform deposit plus the remainder paid to us.',
    ];
    if (s.pending) lines.push(`Deposit on the platform, remainder not paid yet: ${s.pending}`);
    if (s.unknown) lines.push(`Escalated, outcome unknown: ${s.unknown}`);
    if (s.cancelled) lines.push(`Cancelled on the platform (not counted): ${s.cancelled}`);
    if (s.last_sale_at) lines.push(`Last sale ${fmtDate(s.last_sale_at)}`);
    return (
      <span className="plat-sold" title={lines.join('\n')}>
        <b>{s.total}</b> sold
        {s.pending ? <span className="plat-sold-pending"> · {s.pending} pending</span> : null}
      </span>
    );
  };

  const changeFilter = (value) => { setFilter(value); store(FILTER_KEY, value); };
  const changeHideFull = (value) => { setHideFull(value); store(HIDE_FULL_KEY, value ? '1' : '0'); };

  const renderCell = (quest, platform) => {
    const key = `${quest.quest_id}|${platform.key}`;
    const rows = cellListings(quest.quest_id, platform.key);
    const stats = ordersFor(quest.quest_id, platform.key);
    const isOpen = openCell === key;
    const pending = busy === key;
    const expected = expectedOn(platform, quest);

    return (
      <td className={`plat-cell${expected ? '' : ' plat-cell-na'}`} key={platform.key}>
        {rows.map((l) => (
          <div className="plat-chip" key={l.id}>
            <button
              className={`plat-status plat-status-${l.status}`}
              title={statusTitle(l.platform, l.status)}
              disabled={!canEdit || busy === `status:${l.id}`}
              onClick={() => canEdit && cycleStatus(l)}
            >
              <span className="plat-status-glyph">{STATUS_GLYPH[l.status] || '·'}</span>
              {STATUS_LABEL[l.status] || l.status}
            </button>
            <span className="plat-chip-body">
              {missing.has(l.id) && (
                <span
                  className="plat-missing"
                  title={`${platform.label} no longer returns this id for the account — deleted there, or it belongs to another account`}
                >
                  not on {platform.label}
                </span>
              )}
            </span>
            {canEdit && (
              <button
                className="plat-x"
                title="Unlink"
                disabled={busy === `del:${l.id}`}
                onClick={() => remove(l)}
              >×</button>
            )}
          </div>
        ))}

        {stats && (
          <button
            type="button"
            className="plat-orders"
            title="Show these orders"
            onClick={() => setOrdersView({ quest, platform })}
          >
            {stats.orders} order{stats.orders === 1 ? '' : 's'}
            {stats.last_order_at ? `, last ${fmtDate(stats.last_order_at)}` : ''}
          </button>
        )}

        {rows.length === 0 && !isOpen && (
          expected
            ? <span className="plat-none">not listed</span>
            : (
              <span
                className="plat-none plat-na"
                title={`${platform.label} sells ${platform.country} quests only — nothing expected here`}
              >—</span>
            )
        )}

        {/* Last line of the cell: the listing link(s) and the replace/paste
            button side by side. The listing title is nearly always the quest
            name again, and the column header already says which id this is,
            so the link shows the bare number; the rest sits in the tooltip. */}
        {!isOpen && (rows.length > 0 || canEdit) && (
          <div className="plat-cell-foot">
            {rows.map((l) => {
              const tip = [l.external_id ? `${platform.idLabel} ${l.external_id}` : null, l.title]
                .filter(Boolean).join(' — ');
              return l.url
                ? (
                  <a key={l.id} href={l.url} target="_blank" rel="noreferrer" className="plat-id-link" title={tip || platform.label}>
                    {l.external_id ? `#${l.external_id}` : 'open'} ↗
                  </a>
                )
                : (
                  <span key={l.id} className="plat-id" title={tip || undefined}>
                    {l.external_id ? `#${l.external_id}` : (l.title || '—')}
                  </span>
                );
            })}
            {canEdit && (
              <button
                className="plat-link-btn"
                onClick={() => { setOpenCell(key); setLinkDraft(''); }}
              >
                {rows.length ? 'replace link' : '+ paste link'}
              </button>
            )}
          </div>
        )}

        {isOpen && (
          <div className="plat-linkbox">
            <input
              type="text"
              autoFocus
              value={linkDraft}
              placeholder={platform.hint}
              disabled={pending}
              onChange={(e) => setLinkDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && linkDraft.trim()) {
                  attachByLink(quest.quest_id, platform.key, linkDraft.trim());
                }
                if (e.key === 'Escape') { setOpenCell(null); setLinkDraft(''); }
              }}
            />
            <button
              className="plat-btn plat-btn-save"
              disabled={pending || !linkDraft.trim()}
              onClick={() => attachByLink(quest.quest_id, platform.key, linkDraft.trim())}
            >
              {pending ? 'reading…' : 'Pull'}
            </button>
            <button
              className="plat-btn"
              disabled={pending}
              onClick={() => { setOpenCell(null); setLinkDraft(''); }}
            >Cancel</button>
          </div>
        )}
      </td>
    );
  };

  if (loading) return <div className="loading">Loading platform listings…</div>;

  const syncErrorList = Object.entries(syncErrors).filter(([, msg]) => msg);
  const hiddenNow = hideFull ? fullCount : 0;

  return (
    <div className="plat-page">
      <div className="plat-bar">
        <h2>Platform Listings</h2>
        <input
          type="search"
          className="plat-filter"
          value={filter}
          placeholder="Filter: quest, id, city, listing title…"
          onChange={(e) => changeFilter(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Escape') return;
            if (filter) changeFilter(''); else e.target.blur();
          }}
        />
        <label
          className="plat-toggle"
          title="A row is full when every column expected for it holds a listing: Sputnik, Tripster, and the site of the quest's own country"
        >
          <input
            type="checkbox"
            checked={hideFull}
            onChange={(e) => changeHideFull(e.target.checked)}
          />
          hide fully mapped
        </label>
        <span className="plat-shown">
          {visible.length} of {quests.length}
          {hiddenNow > 0 && <> · {hiddenNow} full hidden</>}
        </span>
        <span className="plat-bar-gap" />
        {canEdit && (
          <button className="plat-btn" disabled={syncing} onClick={sync}>
            {syncing ? 'pulling…' : 'Refresh from platforms'}
          </button>
        )}
        <LinkButton className="plat-btn plat-btn-close" href={closeHref} onClick={onClose} title="Esc">
          Close
        </LinkButton>
      </div>

      <p className="plat-sub plat-intro">
        Which listing each quest is sold under. The auto-answer bot matches an incoming
        order by the listing's own id — Sputnik <b>activity id</b>, Tripster <b>experience
        id</b>, a site's <b>product id</b> — all of which come inside the order payload.
        Sputnik and Tripster are pulled on every open: titles and statuses here are what
        the accounts show right now. istanbul.quest takes Turkey, moroccan.quest takes
        Morocco; for other countries those columns stay empty. Test quests appear once
        they hold a listing.
        {!canEdit && <> Editing needs push-to-prod permission — read-only for you.</>}
      </p>

      {error && <div className="plat-error">{error}</div>}
      {salesWarning && <div className="plat-error"><b>Sales count:</b> {salesWarning}</div>}
      {syncErrorList.map(([platform, msg]) => (
        <div className="plat-error" key={platform}>
          <b>{platform === 'sync' ? 'Pull' : platformOf(platform).label}:</b> {msg}
        </div>
      ))}

      <table className="plat-table">
        <thead>
          <tr>
            <th className="plat-citycol-head">City</th>
            <th>Quest</th>
            {PLATFORMS.map((p) => (
              <th key={p.key}>
                <span className={`plat-platform plat-platform-${p.key}`}>{p.label}</span>
                <span
                  className="plat-count"
                  title={`${totals[p.key].have} listed of ${totals[p.key].expected} quests expected on ${p.label}`}
                >
                  {totals[p.key].have}/{totals[p.key].expected}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.length === 0 && (
            <tr className="plat-row-empty">
              <td colSpan={2 + PLATFORMS.length}>
                {quests.length === 0
                  ? 'No quests.'
                  : hideFull && !filter.trim()
                    ? 'Every row is fully mapped — untick "hide fully mapped" to see them.'
                    : 'No quest matches the filter.'}
              </td>
            </tr>
          )}
          {visible.map((q) => {
            const mapped = listings.some((l) => l.quest_id === q.quest_id && l.external_id);
            const group = cityGroups.get(q.quest_id);
            const cls = [
              mapped ? '' : 'plat-row-bare',
              q.is_test ? 'plat-row-test' : '',
              q.quest_id === firstTestRow ? 'plat-row-test-first' : '',
            ].filter(Boolean).join(' ');
            return (
              <tr key={q.quest_id} className={cls}>
                {group && (
                  <th scope="rowgroup" className="plat-citycol" rowSpan={group.span}>
                    <span className="plat-citycol-text">
                      {group.city || '—'}
                    </span>
                  </th>
                )}
                <th scope="row" className="plat-quest">
                  <span className="plat-quest-name">
                    {q.quest_name || '(unnamed)'}
                    {q.is_test && (
                      <span
                        className="plat-test"
                        title={`Lives in quests_test only — the bot escalates orders instead of granting access. `
                          + `On push to prod its listings switch to ${prodIdOf(q.quest_id)} and the bot starts delivering.`}
                      >test</span>
                    )}
                  </span>
                  <code>{q.quest_id}</code>
                  {q.country && <span className="plat-city">{q.country}</span>}
                  {renderSold(q)}
                </th>
                {PLATFORMS.map((p) => renderCell(q, p))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {PLATFORMS.filter((p) => p.account && available[p.key]).map((p) => {
        const all = available[p.key];
        const unmapped = all.filter((e) => !e.quest_id);
        return (
          <div className="plat-panel" key={p.key}>
            <h3>{p.label} account — {all.length} listing{all.length === 1 ? '' : 's'}</h3>
            {unmapped.length === 0 ? (
              <p className="plat-sub">Every listing on the account is mapped to a quest.</p>
            ) : (
              <p className="plat-sub">
                Not mapped yet — find the quest each belongs to (type a name, id or city)
                and it is stored at once.
              </p>
            )}
            {unmapped.map((e) => (
              <div className="plat-avail" key={e.external_id}>
                <code>{e.external_id}</code>
                <span
                  className={`plat-status plat-status-${e.status}`}
                  title={e.platform_status ? `${p.label} says: ${e.platform_status}` : undefined}
                >
                  <span className="plat-status-glyph">{STATUS_GLYPH[e.status] || '·'}</span>
                  {STATUS_LABEL[e.status] || e.status}
                </span>
                <span className="plat-avail-title">
                  {e.url
                    ? <a href={e.url} target="_blank" rel="noreferrer">{e.title || `id ${e.external_id}`}</a>
                    : (e.title || `id ${e.external_id}`)}
                  {e.city && <span className="plat-avail-city">{e.city}</span>}
                </span>
                <QuestPicker
                  options={questOptions}
                  disabled={!canEdit || busy !== null}
                  onPick={(qid) => attachById(qid, p.key, e)}
                />
              </div>
            ))}
          </div>
        );
      })}

      {ordersView && (
        <OrdersModal quest={ordersView.quest} platform={ordersView.platform} onClose={closeOrders} />
      )}

      {orphans.length > 0 && (
        <div className="plat-panel">
          <h3>Listings pointing at quests that exist nowhere</h3>
          <p className="plat-sub">
            The order would be recognised, but no quest with that id exists in <code>quests</code>
            or <code>quests_test</code>, so there is nothing to grant access to.
          </p>
          {orphans.map((l) => (
            <div className="plat-avail" key={l.id}>
              <span className={`plat-platform plat-platform-${l.platform}`}>
                {platformOf(l.platform).label}
              </span>
              <code>{l.quest_id}</code>
              <span className="plat-avail-title">{l.title || l.external_id}</span>
              {canEdit && (
                <button className="plat-btn plat-btn-danger" onClick={() => remove(l)}>Delete</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlatformListings;
